/**
 * Persistent storage for the client's bearer token.
 *
 * The token is deliberately NOT a cookie: a cookie is bound to the origin that set it, and this
 * client talks to a *rotating* API domain that changes as domains are blocked and re-registered.
 * A cookie set on yesterday's domain is useless against today's. So the token lives in IndexedDB
 * on the install origin and is attached explicitly as an `Authorization: Bearer` header to
 * whichever API domain discovery currently trusts (see `authorized-fetch.ts`).
 */
export interface TokenStore {
  /** Forget the stored token (logout / auth failure). */
  clear: () => Promise<void>
  /** The stored bearer token, or `null` if none has been saved. */
  load: () => Promise<null | string>
  /** Persist a bearer token so it survives reloads. */
  save: (token: string) => Promise<void>
}

const DB_NAME = 'vn-pwa-auth'
const DB_VERSION = 1
const STORE_NAME = 'tokens'
const TOKEN_KEY = 'bearer'

/** Open (and, on first use, create) the auth database. */
const openDb = (factory: IDBFactory): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = factory.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = (): void => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME)
    }
    request.onsuccess = (): void => resolve(request.result)
    request.onerror = (): void => reject(request.error)
  })

/** Run one transaction against the token object store and resolve with its request's result. */
const runInStore = async <T>(
  factory: IDBFactory,
  mode: IDBTransactionMode,
  operate: (store: IDBObjectStore) => IDBRequest,
): Promise<T> => {
  const db = await openDb(factory)
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = operate(db.transaction(STORE_NAME, mode).objectStore(STORE_NAME))
      request.onsuccess = (): void => resolve(request.result as T)
      request.onerror = (): void => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * Resolve the global `IDBFactory`, never throwing. In sandboxed in-app/WebView browsers — the VN
 * target — even *reading* the `indexedDB` global can raise a SecurityError; and because a default
 * parameter is evaluated at the call site before the callee's own try/catch runs, that read must
 * be guarded here rather than left as a bare `= indexedDB` default. Mirrors `resolveStorage` in the
 * localStorage siblings (`last-known-index.ts`, `api-domain-persistence.ts`). Returns `undefined`
 * when IndexedDB is unavailable; the boot path (`runtime-api.ts`) reads that as a defined
 * `unavailable` state instead of stranding the shell on 'discovering'.
 */
export const resolveIndexedDb = (): IDBFactory | undefined => {
  try {
    return indexedDB
  } catch {
    return undefined
  }
}

/**
 * Create a bearer-token store backed by IndexedDB. The `IDBFactory` is injected (defaulting to the
 * guarded global read) so the storage glue is exercised end-to-end in tests with an in-memory
 * `fake-indexeddb` factory. When no factory is available — a sandboxed browser blocked the global
 * read — the store degrades to a no-op (`load` yields `null`, `save`/`clear` resolve) so *creating*
 * it never throws; the boot path decides separately to surface `unavailable` in that case.
 */
export const createIndexedDbTokenStore = (factory: IDBFactory | undefined = resolveIndexedDb()): TokenStore => ({
  clear: (): Promise<void> =>
    factory ? runInStore(factory, 'readwrite', (store) => store.delete(TOKEN_KEY)) : Promise.resolve(),
  load: async (): Promise<null | string> => {
    if (!factory) return null
    const value = await runInStore<string | undefined>(factory, 'readonly', (store) => store.get(TOKEN_KEY))
    return value ?? null
  },
  save: (token: string): Promise<void> =>
    factory ? runInStore(factory, 'readwrite', (store) => store.put(token, TOKEN_KEY)) : Promise.resolve(),
})
